import { CompactText } from 'ui/CompactText';
import { useEffect, useRef, useState } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { parsePositiveInt } from 'utils/parsePositiveInt';
import { isAxiosError } from 'axios';

import { StarRating } from 'components/StarRating';
import { useBulletin } from 'hooks/useBulletin';
import {
  STUDENT_TUTOR_PAGE_SIZE,
  useCreateStudentProposal,
  useSearchTutorsByBulletin,
} from 'hooks/useStudentTutorSearch';
import type { TutorSearchItem } from 'api/v1/requests/bulletins';
import { addNotification } from 'notifications';
import { buildChatUrl } from 'utils/buildChatUrl';
import { Button } from 'ui';
import { AcceptIconTeacherActive } from 'ui/icons/Accept.Icon';
import { Overlay } from 'ui/Overlay/Overlay';
import { useStudentTutorSort } from '../StudentTutorSortContext';

import avatar from './../../../assets/images/people.png';
import screen from '../StudentTaskScreen.module.scss';
import {
  formatBulletinGrades,
  formatBulletinSubjectTitle,
  translateStudyLocations,
} from 'utils/bulletinLabels';
import styles from './DetailsSearch.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';
import { DetailsMenuSlot } from 'modules/Details/DetailsMenu/DetailsMenu';

function formatPriceRubPerHour(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return ' - ';
  return `${Math.round(price).toLocaleString('ru-RU')} руб/час`;
}

function tutorCardTitle(t: TutorSearchItem): string {
  return t.name?.trim() || 'Репетитор';
}

function formatEducation(educations?: TutorSearchItem['educations']): string {
  if (!educations || educations.length === 0) return '';

  return educations
    .map((edu) => {
      const row = edu as Record<string, unknown>;
      const startYear = row.startYear ?? row.start_year;
      const endYear = row.endYear ?? row.end_year;
      const period = [startYear, endYear].filter(Boolean).join('-');
      const inst = (row.university || row.institution || '') as string;
      const faculty = (row.faculty || '') as string;
      const specialization = (row.specialization || '') as string;
      const details = [inst, faculty, specialization].filter(Boolean).join(', ');
      return period ? `${period}: ${details}` : details;
    })
    .join('\n');
}

function tutorDescription(t: TutorSearchItem): string {
  return t.description?.trim() || '';
}

function tutorReviewAvg(t: TutorSearchItem): number | null {
  if (typeof t.total_reviews === 'number' && t.total_reviews > 0) {
    if (typeof t.review_avg === 'number' && Number.isFinite(t.review_avg)) {
      return t.review_avg;
    }
  }
  return null;
}

function tutorHasThread(item: TutorSearchItem): boolean {
  return Boolean(
    item.chat_id ||
      item.student_proposal_id ||
      item.bulletin_response_id ||
      item.student_proposal_status,
  );
}

function tutorReviewCount(t: TutorSearchItem): number {
  if (typeof t.total_reviews === 'number' && Number.isFinite(t.total_reviews)) {
    return Math.max(0, t.total_reviews);
  }
  return 0;
}

const StudentSearchContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef<HTMLDivElement>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const { sortPreset } = useStudentTutorSort();

  const bulletinId = parsePositiveInt(id) ?? 0;
  const validBulletin = bulletinId > 0;

  const pageFromUrl = Math.max(0, Number(searchParams.get('page') || '1') - 1);

  const setPage = (pageIndex: number) => {
    const next = new URLSearchParams(searchParams);
    if (pageIndex <= 0) {
      next.delete('page');
    } else {
      next.set('page', String(pageIndex + 1));
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setPage(0);
    // Reset only when sort/bulletin changes - not on every page flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortPreset, bulletinId]);

  const { data: bulletin, isLoading: bulletinLoading } = useBulletin(bulletinId);
  const {
    data: searchPage,
    isLoading: tutorsLoading,
    isError: tutorsError,
    error: tutorsErr,
  } = useSearchTutorsByBulletin(bulletinId, sortPreset, pageFromUrl);

  const createProposal = useCreateStudentProposal(bulletinId);

  const items = searchPage?.items ?? [];
  const total = searchPage?.total ?? 0;
  const totalPages = Math.ceil(total / STUDENT_TUTOR_PAGE_SIZE);
  const currentPage = pageFromUrl + 1;
  const page = pageFromUrl;

  useEffect(() => {
    if (totalPages > 0 && page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const onScroll = () => {
      const bottomReached = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
      setShowOverlay(!bottomReached);
    };
    onScroll();
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length, page]);

  const handlePropose = async (t: TutorSearchItem) => {
    if (t.can_invite === false) {
      addNotification(
        t.invite_block_reason || 'Репетитор пока не может взять заявки этого типа.',
        'error',
      );
      return;
    }
    try {
      const response = await createProposal.mutateAsync({
        tutor_profile_id: t.profile_id,
      });
      addNotification('Приглашение отправлено репетитору', 'success');

      if (response && response.chat_id) {
        navigate(
          buildChatUrl({
            chatId: response.chat_id,
            roomId: response.room_id,
            bulletinId,
            userId: response.tutor_id ?? t.user_id,
            role: 'student',
          }),
        );
      } else {
        addNotification(
          'Приглашение отправлено. Чат откроется, когда репетитор ответит.',
          'info',
        );
      }
    } catch (e: unknown) {
      let msg = 'Не удалось отправить приглашение';
      if (isAxiosError(e)) {
        const d = e.response?.data as { detail?: string } | undefined;
        if (typeof d?.detail === 'string') msg = d.detail;
      }
      addNotification(msg, 'error');
    }
  };

  const openDetails = (t: TutorSearchItem) => {
    navigate(`/profile/details/${bulletinId}/rep/${t.user_id}`, {
      state: t,
    });
  };

  if (!validBulletin) {
    return (
      <div className={screen.page}>
        <h1 className={screen.title}>Репетиторы по задаче</h1>
        <DetailsMenuSlot />
        <p className={screen.body}>Некорректный адрес: не указан id заявки.</p>
      </div>
    );
  }

  const budgetHint = (() => {
    if (!bulletin) return '';
    const from = bulletin.price_from;
    const to = bulletin.price_to;
    const money =
      from && to && from !== to
        ? `${from} - ${to} руб/час`
        : from
          ? `${from} руб/час`
          : to
            ? `${to} руб/час`
            : '';
    const parts = [
      formatBulletinSubjectTitle(bulletin.subject_category),
      formatBulletinGrades(bulletin),
      translateStudyLocations(bulletin.study_locations),
    ].filter(Boolean);
    const budget = money ? `Бюджет ${money} - ориентир, не установленная цена.` : '';
    return [parts.join(' / '), budget].filter(Boolean).join('. ');
  })();

  const notApproved =
    bulletin &&
    bulletin.verification_status &&
    bulletin.verification_status !== 'approved';

  return (
    <div className={`${styles['main-content']} ${screen.page}`}>
      <PageHead crumb='Личный кабинет / Мои задачи' title='Репетиторы по задаче' />
      <DetailsMenuSlot />
      <div className={styles['teacher-search__header']}>
        {totalPages > 1 ? (
          <nav className={styles['teacher-search__pagination']} aria-label='Страницы'>
            <button
              type='button'
              className={styles['teacher-search__pagination-prev']}
              disabled={currentPage === 1}
              onClick={() => setPage(page - 1)}
              aria-label='Предыдущая страница'
            />
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
              const pageNum = idx + 1;
              const isSelected = currentPage === pageNum;
              return (
                <button
                  key={pageNum}
                  type='button'
                  className={`${styles['teacher-search__pagination-page']} ${
                    isSelected ? styles['teacher-search__pagination-page--active'] : ''
                  }`}
                  aria-current={isSelected ? 'page' : undefined}
                  onClick={() => setPage(idx)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 ? (
              <span className={styles['teacher-search__pagination-ellipsis']} aria-hidden='true'>
                ...
              </span>
            ) : null}
            <button
              type='button'
              className={styles['teacher-search__pagination-next']}
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage)}
              aria-label='Следующая страница'
            />
          </nav>
        ) : null}
      </div>
      {bulletinLoading && <p>Загрузка заявки...</p>}
      {budgetHint ? <CompactText className={screen.note} tone={'info'}>{budgetHint}</CompactText> : null}
      {notApproved && (
        <CompactText className={screen.note} tone={'info'}>
          Заявка еще не одобрена модерацией. Отправить приглашение репетитору можно после
          одобрения.
        </CompactText>
      )}
      {tutorsError && (
        <p
          style={{
            color: '#c33',
            marginBottom: '12px',
            padding: '15px',
            marginRight: '30px',
            textAlign: 'center',
            borderRadius: '20px',
            backgroundColor: 'rgb(180, 121, 121)',
          }}
        >
          Ошибка загрузки списка:{' '}
          {isAxiosError(tutorsErr)
            ? (tutorsErr.response?.data as { detail?: string })?.detail || tutorsErr.message
            : String(tutorsErr)}
        </p>
      )}
      <div className={styles['teacher-search']} ref={listRef}>
        {tutorsLoading && <BrandLoader center label='Загрузка...' />}
        {!tutorsLoading && items.length === 0 && !tutorsError && !notApproved && (
          <p>По этой заявке пока нет подходящих репетиторов.</p>
        )}

        {items.map((item, index) => {
          const hasResponded = tutorHasThread(item);
          const canInvite = item.can_invite !== false;
          const showUnavailableSplit =
            !canInvite &&
            !hasResponded &&
            (index === 0 ||
              items[index - 1].can_invite !== false ||
              tutorHasThread(items[index - 1]));
          const inviteHint =
            !hasResponded && !canInvite
              ? (item.invite_block_reason ||
                'Репетитор пока не может взять заявки этого типа.')
              : null;
          const proposeLabel = hasResponded ? 'В чат' : 'Предложить';

          const eduText = formatEducation(item.educations);
          const descText = tutorDescription(item);
          const fullInfoText =
            [eduText, descText].filter(Boolean).join('\n\n') || 'Информация не указана';

          const reviewAvg = tutorReviewAvg(item);
          const reviewCount = tutorReviewCount(item);
          const hasReviews = reviewCount > 0 && reviewAvg != null;

          return (
            <div
              key={`${item.profile_id}-${item.user_id}`}
              className={`${styles['teacher-search__item']}${
                !canInvite && !hasResponded
                  ? ` ${styles['teacher-search__item--unavailable']}`
                  : ''
              }`}
            >
              {showUnavailableSplit ? (
                <p className={styles['teacher-search__invite-split']}>
                  Эти репетиторы пока не могут взять эту заявку
                </p>
              ) : null}
              <div className={styles['teacher-search__item-header']}>
                <img
                  src={item.profile_avatar_url || avatar}
                  alt=''
                  onError={(e) => {
                    e.currentTarget.src = avatar;
                  }}
                />
                <div className={styles['teacher-search__item-header-content']}>
                  <h3>{tutorCardTitle(item)}</h3>
                  <p className={screen.badge}>Анкета репетитора</p>
                  <div
                    className={styles['teacher-search__item-header-rating']}
                    aria-label={
                      hasReviews
                        ? `Рейтинг ${reviewAvg.toFixed(1)}, ${reviewCount} отзывов`
                        : 'Нет отзывов'
                    }
                  >
                    {hasReviews ? (
                      <>
                        <StarRating value={reviewAvg} size='sm' showValue />
                        <span>
                          {reviewCount}{' '}
                          {reviewCount === 1
                            ? 'отзыв'
                            : reviewCount >= 2 && reviewCount <= 4
                              ? 'отзыва'
                              : 'отзывов'}
                        </span>
                      </>
                    ) : (
                      <span>Рейтинг не указан. Реальные отзывы не загружены.</span>
                    )}
                  </div>
                </div>
                {item.is_verified ? <AcceptIconTeacherActive /> : null}
              </div>

              <span className={styles['teacher-search__item-price']}>
                {formatPriceRubPerHour(item.current_service_price)}
              </span>

              <div className={styles['teacher-search__item-info']}>
                <h4>Образование и опыт работы:</h4>
                <p
                  className={styles['teacher-search__item-content']}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {fullInfoText}
                </p>
              </div>

              <div className={styles['teacher-search__item-bottom']}>
                <div className={styles['teacher-search__item-bottom-btns']}>
                  <Button
                    variant='filled'
                    size='sm'
                    color={hasResponded ? 'secondary' : 'primary'}
                    shape='brick'
                    disabled={(!!notApproved && !hasResponded) || (!hasResponded && !canInvite)}
                    loading={createProposal.isPending}
                    onClick={() => {
                      if (hasResponded) {
                        if (item.chat_id) {
                          navigate(
                            buildChatUrl({
                              chatId: item.chat_id,
                              roomId: item.room_id,
                              bulletinId,
                              userId: item.user_id,
                              role: 'student',
                            }),
                          );
                        } else if (item.bulletin_response_id) {
                          navigate(
                            `/profile/details/${bulletinId}/chats?responseId=${item.bulletin_response_id}&userId=${item.user_id}`,
                          );
                        } else {
                          addNotification(
                            'Чат еще не создан. Дождитесь ответа репетитора.',
                            'info',
                          );
                        }
                      } else {
                        handlePropose(item);
                      }
                    }}
                  >
                    {proposeLabel}
                  </Button>
                  <Button
                    variant='filled'
                    size='sm'
                    color='secondary'
                    shape='brick'
                    onClick={() => openDetails(item)}
                  >
                    Посмотреть анкету
                  </Button>
                </div>
              </div>
              {inviteHint ? (
                <p className={styles['teacher-search__item-invite-hint']}>{inviteHint}</p>
              ) : null}
            </div>
          );
        })}
        <Overlay showOverlay={showOverlay && items.length > 0} />
      </div>
    </div>
  );
};

export const StudentSearch = () => {
  return (
    <main className={styles.main}>
      <StudentSearchContent />
    </main>
  );
};
